import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";

import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import DialogIncentivoResultadoDetalhe from "pages/comercial/components/DialogIncentivoResultadoDetalhe";

import {
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function PaperComponent(props) {
  return (
    <Draggable
      handle=".draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], input, textarea, select, button'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function toISO(d) {
  if (!d) return "";

  const value = String(d).trim();
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);

  return match ? match[1] : value;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numFmt(v) {
  const n = safeNum(v);

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function estadoInfo(row) {
  if (row?.atingido === true) {
    return {
      label: "Atingido",
      color: "#4caf50",
    };
  }

  const temMetricasPrincipais =
    row?.qtd_principal !== undefined || row?.valor_principal !== undefined;

  const temVendas = temMetricasPrincipais
    ? safeNum(row?.qtd_principal) > 0 || safeNum(row?.valor_principal) > 0
    : safeNum(row?.qtd) > 0 || safeNum(row?.valor) > 0;

  if (temVendas) {
    return {
      label: "Em progresso",
      color: "#f9a825",
    };
  }

  return {
    label: "Sem vendas",
    color: "#d32f2f",
  };
}

function EstadoResumoItem({ color, value, label }) {
  return (
    <Tooltip title={`${label}: ${value}`}>
      <MDBox
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 11,
            height: 11,
            minWidth: 11,
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />

        <MDTypography
          variant="caption"
          color="text"
          sx={{
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {value}
        </MDTypography>
      </MDBox>
    </Tooltip>
  );
}

function IncentivoResultadosTable({ data = [], mostrarVendedor = false, onAbrirDetalhe }) {
  const rowsData = Array.isArray(data) ? data : [];

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "estado_visual",
        accessor: "estado_visual",
        filterable: false,
        sortable: false,
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        resizable: false,
        Cell: ({ row }) => {
          const estado = estadoInfo(row.original);

          return (
            <Tooltip title={estado.label}>
              <span
                style={{
                  display: "inline-block",
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  backgroundColor: estado.color,
                }}
              />
            </Tooltip>
          );
        },
      },

      ...(mostrarVendedor
        ? [
            {
              Header: "Vendedor",
              accessor: "vendedor",
              width: 180,
            },
          ]
        : []),

      {
        Header: "Grupo",
        accessor: "grupo",
        width: 180,
      },
      {
        Header: "Patamar",
        accessor: "patamar",
        width: 180,
      },
      {
        Header: "Condições necessárias",
        accessor: "condicoes",
        width: 260,
      },
      {
        Header: "Prémio",
        accessor: "beneficio_descricao",
        width: 180,
      },
      {
        Header: "Faturação",
        accessor: "obtido",
        width: 150,
      },
      {
        Header: "Estado",
        id: "estado",
        accessor: (row) => estadoInfo(row).label,
        width: 130,
        Cell: ({ row }) => {
          const estado = estadoInfo(row.original);

          return (
            <span
              style={{
                fontWeight: 600,
                color: estado.color,
              }}
            >
              {estado.label}
            </span>
          );
        },
      },
      {
        Header: "Prémio alcançado",
        accessor: "beneficio_obtido",
        width: 150,
        sortType: "numPt",
        align: "center",
        Cell: ({ value }) =>
          value === null || value === undefined || value === "" ? "" : numFmt(value),
      },
    ],
    [mostrarVendedor]
  );

  const rowActions = useMemo(
    () => [
      {
        id: "detalhe",
        icon: <InfoOutlinedIcon fontSize="small" />,
        tooltip: "Ver clientes e faturas",
        color: "info",
        onClick: (row) => onAbrirDetalhe?.(row),
        visible: () => typeof onAbrirDetalhe === "function",
      },
    ],
    [onAbrirDetalhe]
  );

  return (
    <ProjectTable
      tableKey={mostrarVendedor ? "dashboard-incentivos-geral" : "dashboard-incentivos-pessoal"}
      version="v1"
      columns={columns}
      data={rowsData}
      rowActions={rowActions}
      actionsPosition="left"
      emptyMessage="Não foram encontrados resultados pessoais neste incentivo."
      minWidth={1180}
      maxHeight={360}
      showKeepFilters
    />
  );
}

function IncentivoDashboardAccordion({ incentivo }) {
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);

  const [openEstadoGeral, setOpenEstadoGeral] = useState(false);
  const [loadingEstadoGeral, setLoadingEstadoGeral] = useState(false);
  const [rowsEstadoGeral, setRowsEstadoGeral] = useState([]);
  const [estadoGeralLoaded, setEstadoGeralLoaded] = useState(false);

  const [openResultadoDetalhe, setOpenResultadoDetalhe] = useState(false);
  const [loadingResultadoDetalhe, setLoadingResultadoDetalhe] = useState(false);
  const [resultadoDetalhe, setResultadoDetalhe] = useState([]);
  const [resultadoDetalheTitulo, setResultadoDetalheTitulo] = useState("");

  const carregarResultadosPessoais = async () => {
    if (loading || loaded) return;

    setLoading(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelIncentivosResultadosVendedores", {
          idincentivo: Number(incentivo?.id || 0),
          idutilizador: userData?.id || 0,
          funcoes: userData?.funcoes || [],
          apenasMeuVendedor: true,
          apenasVigentesOuRecentes: true,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      setRows(Array.isArray(payload) ? payload : []);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const abrirEstadoGeral = async () => {
    if (!incentivo?.pode_ver_estado_geral) return;

    setOpenEstadoGeral(true);

    if (estadoGeralLoaded || loadingEstadoGeral) return;

    setLoadingEstadoGeral(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelIncentivosResultadosVendedores", {
          idincentivo: Number(incentivo?.id || 0),
          idutilizador: userData?.id || 0,
          funcoes: userData?.funcoes || [],
          apenasMeuVendedor: false,
          apenasVigentesOuRecentes: true,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const lista = Array.isArray(payload) ? payload : [];

      const listaOrdenada = [...lista].sort((a, b) => {
        const vendedorA = String(a?.vendedor || "").trim();
        const vendedorB = String(b?.vendedor || "").trim();

        const comparacaoVendedor = vendedorA.localeCompare(vendedorB, "pt-PT", {
          sensitivity: "base",
        });

        if (comparacaoVendedor !== 0) return comparacaoVendedor;

        const grupoA = String(a?.grupo || "").trim();
        const grupoB = String(b?.grupo || "").trim();

        const comparacaoGrupo = grupoA.localeCompare(grupoB, "pt-PT", {
          sensitivity: "base",
        });

        if (comparacaoGrupo !== 0) return comparacaoGrupo;

        const ordemA = safeNum(a?.ordem_patamar);
        const ordemB = safeNum(b?.ordem_patamar);

        if (ordemA !== ordemB) return ordemA - ordemB;

        return String(a?.patamar || "").localeCompare(String(b?.patamar || ""), "pt-PT", {
          sensitivity: "base",
        });
      });

      setRowsEstadoGeral(listaOrdenada);
      setEstadoGeralLoaded(true);
    } finally {
      setLoadingEstadoGeral(false);
    }
  };

  const abrirResultadoDetalhe = async (row) => {
    if (!row) return;

    setLoadingResultadoDetalhe(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelIncentivosResultadoDetalhe", {
          idincentivo: row.idincentivo || incentivo?.id || 0,
          iddesafio: row.iddesafio,
          idpatamar: row.idpatamar,
          refvendedor: row.refvendedor,
          datainicial: toISO(incentivo?.dataini) || null,
          datafinal: toISO(incentivo?.datafim) || null,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      setResultadoDetalhe(Array.isArray(payload) ? payload : []);

      setResultadoDetalheTitulo(
        [row?.vendedor, row?.grupo, row?.patamar].filter(Boolean).join(" · ")
      );

      setOpenResultadoDetalhe(true);
    } finally {
      setLoadingResultadoDetalhe(false);
    }
  };

  const resumoEstadoGeral = useMemo(() => {
    if (!estadoGeralLoaded) return null;

    const vendedores = new Map();

    rowsEstadoGeral.forEach((row) => {
      const ref = String(row?.refvendedor || "").trim() || String(row?.vendedor || "").trim();

      if (!ref) return;

      const estado = estadoInfo(row);

      if (!vendedores.has(ref)) {
        vendedores.set(ref, {
          atingido: false,
          emProgresso: false,
        });
      }

      const vendedor = vendedores.get(ref);

      if (estado.label === "Atingido") {
        vendedor.atingido = true;
      } else if (estado.label === "Em progresso") {
        vendedor.emProgresso = true;
      }
    });

    let atingidos = 0;
    let emProgresso = 0;
    let semVendas = 0;

    vendedores.forEach((vendedor) => {
      if (vendedor.atingido) {
        atingidos += 1;
      } else if (vendedor.emProgresso) {
        emProgresso += 1;
      } else {
        semVendas += 1;
      }
    });

    return {
      total: vendedores.size,
      atingidos,
      emProgresso,
      semVendas,
    };
  }, [estadoGeralLoaded, rowsEstadoGeral]);

  const periodo = [toISO(incentivo?.dataini), toISO(incentivo?.datafim)]
    .filter(Boolean)
    .join(" a ");

  const observacoes = String(incentivo?.obs || "").trim();

  const subtitle = [periodo, observacoes].filter(Boolean).join(" · ");

  const temVendedorAssociado = Boolean(String(userData?.refvendedor || "").trim());
  const podeVerEstadoGeral = incentivo?.pode_ver_estado_geral === true;

  const headerResumo =
    podeVerEstadoGeral && resumoEstadoGeral ? (
      <MDBox
        display="flex"
        alignItems="center"
        gap={1.25}
        flexWrap="wrap"
        sx={{
          px: { xs: 0, md: 0.5 },
        }}
      >
        <Tooltip title="Total de vendedores">
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {resumoEstadoGeral.total} vendedores
          </MDTypography>
        </Tooltip>

        <EstadoResumoItem color="#4caf50" value={resumoEstadoGeral.atingidos} label="Atingiram" />

        <EstadoResumoItem
          color="#f9a825"
          value={resumoEstadoGeral.emProgresso}
          label="Em progresso"
        />

        <EstadoResumoItem color="#d32f2f" value={resumoEstadoGeral.semVendas} label="Sem vendas" />
      </MDBox>
    ) : null;

  return (
    <DashboardLazyAccordion
      title={incentivo?.nome || "Incentivo"}
      subtitle={subtitle}
      loading={loading}
      loaded={loaded}
      showRefreshButton={false}
      headerContent={headerResumo}
      onExpand={carregarResultadosPessoais}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        gap={1}
        mb={podeVerEstadoGeral ? 1.5 : 0}
      >
        {podeVerEstadoGeral && (
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={abrirEstadoGeral}
            disabled={loadingEstadoGeral}
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {loadingEstadoGeral ? (
              <>
                <CircularProgress size={15} color="inherit" sx={{ mr: 1 }} />A carregar...
              </>
            ) : (
              "Ver estado geral"
            )}
          </MDButton>
        )}
      </MDBox>

      {temVendedorAssociado ? (
        <IncentivoResultadosTable data={rows} onAbrirDetalhe={abrirResultadoDetalhe} />
      ) : (
        <MDBox
          py={2}
          px={1}
          sx={{
            borderRadius: 1,
            backgroundColor: "rgba(0,0,0,0.02)",
          }}
        >
          <MDTypography
            variant="button"
            color="text"
            sx={{
              display: "block",
              textAlign: "center",
              fontWeight: "regular",
              whiteSpace: "normal",
            }}
          >
            Este utilizador não está associado a um vendedor e não tem resultados pessoais neste
            incentivo.
          </MDTypography>
        </MDBox>
      )}

      <Dialog
        open={openEstadoGeral}
        onClose={() => setOpenEstadoGeral(false)}
        maxWidth="xl"
        fullWidth
        PaperComponent={PaperComponent}
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 24px)", xl: "1500px" },
            maxWidth: "100%",
            m: { xs: 1.5, md: 3 },
          },
        }}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Estado geral — {incentivo?.nome || "Incentivo"}
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 1.5, md: 3 },
            maxWidth: "100%",
            overflowX: "hidden",
          }}
        >
          {loadingEstadoGeral && !estadoGeralLoaded ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox mt={1}>
              <IncentivoResultadosTable
                data={rowsEstadoGeral}
                mostrarVendedor
                onAbrirDetalhe={abrirResultadoDetalhe}
              />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenEstadoGeral(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogIncentivoResultadoDetalhe
        PaperComponent={PaperComponent}
        open={openResultadoDetalhe}
        onClose={() => setOpenResultadoDetalhe(false)}
        titulo={resultadoDetalheTitulo}
        data={resultadoDetalhe}
        loading={loadingResultadoDetalhe}
      />
    </DashboardLazyAccordion>
  );
}

export default function IncentivosVendedorDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
  onEmptyChange,
}) {
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [incentivos, setIncentivos] = useState([]);
  const [carregado, setCarregado] = useState(false);

  const canSee = !!userData?.id;

  const carregarIncentivos = async () => {
    if (!canSee) {
      setIncentivos([]);
      setCarregado(true);

      onEmptyChange?.(true);
      onLoadedChange?.(true);
      onLoadingChange?.(false);

      return;
    }

    setLoading(true);
    onLoadingChange?.(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelIncentivosDashboard", {
          idutilizador: userData?.id || 0,
          funcoes: userData?.funcoes || [],
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const lista = Array.isArray(payload) ? payload : [];

      setIncentivos(lista);
      setCarregado(true);

      onEmptyChange?.(lista.length === 0);
      onLoadedChange?.(true);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) return;

    carregarIncentivos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken, canSee, userData?.id]);

  if (!canSee) return null;

  return (
    <MDBox
      sx={{
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {loading && carregado && (
        <MDBox py={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </MDBox>
      )}

      {carregado && incentivos.length > 0 && (
        <MDBox
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {incentivos.map((incentivo) => (
            <IncentivoDashboardAccordion
              key={`incentivo-dashboard-${incentivo.id}-${refreshToken}`}
              incentivo={incentivo}
            />
          ))}
        </MDBox>
      )}

      {carregado && incentivos.length === 0 && !loading && (
        <MDBox py={2}>
          <MDTypography
            variant="button"
            color="text"
            sx={{
              display: "block",
              textAlign: "center",
              fontWeight: "regular",
            }}
          >
            Não foram encontrados incentivos para apresentar.
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );
}
